// utils/feedsTransformer.js
// Utility to automatically transform existing feedsConfig to mobile format

import { getMobileFeedsForCanadaExecutive } from "./multiCountryConfig.js";

// Default fallback function for API calls
const DefaultApiFunction = () => <div>Feed function not available</div>;

/**
 * Transform the existing feedsConfig structure to the mobile format used by NewCarousels
 * @param {Object} feedsConfig - Original feedsConfig object
 * @returns {Object} - Transformed mobile feeds config
 */
export const transformFeedsToMobile = (feedsConfig) => {
  const mobileFeedsConfig = {};

  Object.keys(feedsConfig).forEach(sectionKey => {
    const sectionFeeds = feedsConfig[sectionKey];
    mobileFeedsConfig[sectionKey] = [];

    // Ensure sectionFeeds is an array before calling forEach
    if (Array.isArray(sectionFeeds)) {
      sectionFeeds.forEach(feed => {
        if (feed.type === 'normal') {
          // Transform normal feeds to mobile carousel format
          // FIXED: Now preserves useCommitteeAPI property for committee feeds
          mobileFeedsConfig[sectionKey].push({
            id: feed.id,
            name: feed.title,
            displayType: 'normal',
            apiParams: feed.apiEndpoint,
            apiFunction: DefaultApiFunction,
            feedBranch: feed.branch,
            feedTopic: feed.topic,
            buttonName: feed.buttonName,
            generateFunction: null,
            showOnHomePage: feed.showOnHomePage,
            useCommitteeAPI: feed.useCommitteeAPI || false,  // ADDED: Preserve committee API flag
            viewAllLink: feed.viewAllLink  // ADDED: Preserve viewAllLink for navigation
          });
        } else if (feed.type === 'carousel') {
          // Transform carousel departments to grouped mobile format
          const groupInfo = {
            group: feed.id,
            groupDisplayName: feed.departmentTitle,
            groupLink: feed.departmentLink,
            showGroupButton: true,
            groupButtonText: `View all ${feed.departmentTitle} news`
          };

          // Process each carousel item (excluding viewall items)
          if (Array.isArray(feed.carouselItems)) {
            feed.carouselItems
              .filter(item => item.type !== 'viewall')
              .forEach(item => {
                // Handle member type items
                if (item.type === 'member') {
                  mobileFeedsConfig[sectionKey].push({
                    id: item.id,
                    name: item.title,
                    type: 'member',
                    memberType: item.memberType,
                    displayType: 'carousel',
                    showOnHomePage: feed.showOnHomePage,
                    ...groupInfo
                  });
                }  else {
                  
                  mobileFeedsConfig[sectionKey].push({
                    id: item.id,
                    name: item.title,
                    displayType: 'carousel',
                    apiParams: item.apiEndpoint,
                    apiFunction: DefaultApiFunction,
                    feedBranch: item.branch,
                    feedTopic: item.topic,
                    buttonName: item.buttonName,
                    generateFunction: null,
                    showOnHomePage: feed.showOnHomePage,
                    useCommitteeAPI: item.useCommitteeAPI || false,  // ADDED: Preserve for carousel items too
                    ...groupInfo
                  });
                }
              });
          }
        }
      });
    }
  });

  return mobileFeedsConfig;
};

/**
 * Get mobile feeds for Canada sections
 */
const getCanadaMobileFeedsForSection = (sectionKey) => {
  const canadaFeedsConfig = require('./canadaFeedsConfig').canadaFeedsConfig;
  const canadaSectionFeeds = canadaFeedsConfig[sectionKey] || [];
  
  return canadaSectionFeeds
    .map(feed => ({
      id: feed.id,
      name: feed.title,
      displayType: 'normal',
      apiParams: feed.apiEndpoint,
      apiFunction: DefaultApiFunction,
      feedBranch: feed.branch,
      feedTopic: feed.topic,
      buttonName: feed.buttonName,
      generateFunction: null,
      showOnHomePage: feed.showOnHomePage,
      useCommitteeAPI: feed.useCommitteeAPI || false  // ADDED
    }));
};

/**
 * Get mobile feeds for a specific section
 * @param {Object} originalFeedsConfig - Original feedsConfig
 * @param {string} sectionKey - Section key (e.g., 'legislative', 'executive')
 * @param {string} countryCode - Country code ('us', 'ca', 'mx', etc.)
 * @returns {Array} - Array of mobile feed objects for the section
 */
export const getMobileFeedsForSection = (originalFeedsConfig, sectionKey, countryCode = 'us') => {
  // Special handling for Canada sections
  if (countryCode === 'ca') {
    if (sectionKey === 'executive') {
      return getCanadaExecutiveMobileFeeds();
    }
    // For other Canada sections, get feeds from canadaFeedsConfig
    return getCanadaMobileFeedsForSection(sectionKey);
  }
  
  // Special handling for Mexico sections
  if (countryCode === 'mx') {
    // Special handling for Mexico executive section if needed
    if (sectionKey === 'executive') {
      return getMexicoExecutiveMobileFeeds();
    }
    
    // For other Mexico sections, get feeds from mexicoFeedsConfig
    return getMexicoMobileFeedsForSection(sectionKey);
  }

  if (countryCode === 'uk') {
    if (sectionKey === 'executive') {
      return getUKExecutiveMobileFeeds();
    }
    return getUKMobileFeedsForSection(sectionKey);
  }
  
  // Use the original transformer for US and other cases
  const mobileFeedsConfig = transformFeedsToMobile(originalFeedsConfig);
  return mobileFeedsConfig[sectionKey] || [];
};

const getCanadaExecutiveMobileFeeds = () => {
  const canadaFeedsConfig = require('./canadaFeedsConfig').canadaFeedsConfig;
  const canadaExecutiveFeeds = canadaFeedsConfig.executive || [];
  
  // Map of Canada executive feed IDs to their department routes
  const agencyRouteMapping = {
    'executiveNews': '/canada/prime-minister',
    'cra': '/canada/canada-revenue-agency',
    'impactAssessment': '/canada/impact-assessment-agency',
    'foodInspection': '/canada/canadian-food-inspection-agency',
    'atlanticOpportunities': '/canada/atlantic-canada-opportunities-agency',
    'feddevOntario': '/canada/federal-economic-development-southern-ontario',
    'borderServices': '/canada/canada-border-services-agency',
    'transportation': '/canada/canadian-transportation-agency',
    'northernEconomicDevelopment': '/canada/canadian-northern-economic-development-agency',
    'financialConsumer': '/canada/financial-consumer-agency',
    'publicHealth': '/canada/public-health-agency',
    'spaceAgency': '/canada/canadian-space-agency'
  };

  return canadaExecutiveFeeds
    .map(feed => {
      const departmentRoute = agencyRouteMapping[feed.id];
      
      return {
        id: feed.id,
        name: feed.title,
        displayType: 'normal',
        apiParams: feed.apiEndpoint,
        feedBranch: feed.branch,
        feedTopic: feed.topic,
        buttonName: feed.buttonName,
        useCommitteeAPI: feed.useCommitteeAPI || false,  // ADDED
        // KEY: These properties enable department navigation
        departmentPage: departmentRoute ? true : false,
        agencyRoute: departmentRoute || null
      };
    });
};

const getMexicoExecutiveMobileFeeds = () => {
  const mexicoFeedsConfig = require('./mexicoFeedsConfig').mexicoFeedsConfig;
  const mexicoExecutiveFeeds = mexicoFeedsConfig.executive || [];
  
  // Map of Mexico executive feed IDs to their department routes
  const agencyRouteMapping = {
    'president': '/mexico/presidencia',
    'segob': '/mexico/secretaria-gobernacion', 
    'sre': '/mexico/secretaria-relaciones-exteriores',
    'shcp': '/mexico/secretaria-hacienda',
    'sedena': '/mexico/secretaria-defensa',
    'semar': '/mexico/secretaria-marina',
    'sspc': '/mexico/secretaria-seguridad',
    'se': '/mexico/secretaria-economia',
    'executiveNews': '/mexico/secretaria-agricultura', // agricultura feed
    'sct': '/mexico/secretaria-comunicaciones',
    'welfare': '/mexico/secretaria-bienestar',
    'salud': '/mexico/secretaria-salud',
    'stps': '/mexico/secretaria-trabajo',
    'semarNat': '/mexico/secretaria-medio-ambiente',
    'sfp': '/mexico/secretaria-funcion-publica',
    'sectur': '/mexico/secretaria-turismo'
  };

  return mexicoExecutiveFeeds
    // FIXED: Don't filter by showOnHomePage for section pages - show all feeds
    .map(feed => {
      const departmentRoute = agencyRouteMapping[feed.id];
      
      return {
        id: feed.id,
        name: feed.title,
        displayType: 'normal',
        apiParams: feed.apiEndpoint,
        feedBranch: feed.branch,
        feedTopic: feed.topic,
        buttonName: feed.buttonName,
        showOnHomePage: feed.showOnHomePage,
        useCommitteeAPI: feed.useCommitteeAPI || false,  // ADDED
        // ADDED: Department navigation for Mexico secretarías
        departmentPage: departmentRoute ? true : false,
        agencyRoute: departmentRoute || null
      };
    });
};

const getMexicoMobileFeedsForSection = (sectionKey) => {
  const mexicoFeedsConfig = require('./mexicoFeedsConfig').mexicoFeedsConfig;
  const mexicoSectionFeeds = mexicoFeedsConfig[sectionKey] || [];
  
  return mexicoSectionFeeds
    // FIXED: Don't filter by showOnHomePage for section pages - show all feeds
    .map(feed => ({
      id: feed.id,
      name: feed.title,
      displayType: 'normal',
      apiParams: feed.apiEndpoint,
      apiFunction: DefaultApiFunction,
      feedBranch: feed.branch,
      feedTopic: feed.topic,
      buttonName: feed.buttonName,
      generateFunction: null,
      showOnHomePage: feed.showOnHomePage,
      useCommitteeAPI: feed.useCommitteeAPI || false  // ADDED
    }));
};

const getUKExecutiveMobileFeeds = () => {
  const unitedKingdomFeedsConfig = require('./unitedKingdomFeedsConfig').unitedKingdomFeedsConfig;
  const ukExecutiveFeeds = unitedKingdomFeedsConfig.executive || [];
  
  // Map of UK executive feed IDs to their department routes
  const agencyRouteMapping = {
    'no10': '/uk/prime-ministers-office',
    'treasury': '/uk/hm-treasury', 
    'ago': '/uk/attorney-generals-office',
    'dcms': null, // No department page configured
    'secretary-scotland': null,
    'dsit': '/uk/department-for-science-innovation-and-technology',
    'cabinetOffice': '/uk/cabinet-office',
    'dft': '/uk/department-for-transport',
    'fcdo': '/uk/foreign-commonwealth-development-office',
    'home-office': '/uk/home-office',
    'dfe': '/uk/department-for-education',
    'mod': '/uk/ministry-of-defence',
    'dbt': '/uk/department-for-business-and-trade',
    'leader-house-commons': null,
    'leader-house-lords': null,
    'desnz': '/uk/department-for-energy-security-and-net-zero',
    'defra': '/uk/department-for-environment-food-rural-affairs',
    'dlu-hc': '/uk/department-for-levelling-up-housing-and-communities',
    'dwp': '/uk/department-for-work-and-pensions',
    'dhsc': '/uk/department-of-health-and-social-care',
    'moj': '/uk/ministry-of-justice',
    'nio': null,
    'wales': null
  };

  return ukExecutiveFeeds
    .map(feed => {
      const departmentRoute = agencyRouteMapping[feed.id];
      
      return {
        id: feed.id,
        name: feed.title,
        displayType: 'normal',
        apiParams: feed.apiEndpoint,
        feedBranch: feed.branch,
        feedTopic: feed.topic,
        buttonName: feed.buttonName,
        showOnHomePage: feed.showOnHomePage,
        useCommitteeAPI: feed.useCommitteeAPI || false,  // ADDED
        departmentPage: departmentRoute ? true : false,
        agencyRoute: departmentRoute || null
      };
    });
};

const getUKMobileFeedsForSection = (sectionKey) => {
  const unitedKingdomFeedsConfig = require('./unitedKingdomFeedsConfig').unitedKingdomFeedsConfig;
  const ukSectionFeeds = unitedKingdomFeedsConfig[sectionKey] || [];
  
  return ukSectionFeeds
    .map(feed => ({
      id: feed.id,
      name: feed.title,
      displayType: 'normal',
      apiParams: feed.apiEndpoint,
      apiFunction: DefaultApiFunction,
      feedBranch: feed.branch,
      feedTopic: feed.topic,
      buttonName: feed.buttonName,
      generateFunction: null,
      showOnHomePage: feed.showOnHomePage,
      useCommitteeAPI: feed.useCommitteeAPI || false  // ADDED
    }));
};

// Rest of existing functions remain unchanged
export const getMobileFeedsByType = (originalFeedsConfig, sectionKey, displayType = 'normal', countryCode = 'us') => {
  const sectionFeeds = getMobileFeedsForSection(originalFeedsConfig, sectionKey, countryCode);
  return sectionFeeds.filter(feed => feed.displayType === displayType);
};

export const transformSingleFeedToMobile = (feed, sectionKey) => {
  if (feed.type === 'normal') {
    return {
      id: feed.id,
      name: feed.title,
      displayType: 'normal',
      apiParams: feed.apiEndpoint,
      apiFunction: () => <div>Feed function not available</div>,
      feedBranch: feed.branch,
      feedTopic: feed.topic,
      buttonName: feed.buttonName,
      generateFunction: null,
      useCommitteeAPI: feed.useCommitteeAPI || false  // ADDED
    };
  } else if (feed.type === 'carousel') {
    const groupInfo = {
      group: feed.id,
      groupDisplayName: feed.departmentTitle,
      groupLink: feed.departmentLink,
      showGroupButton: true,
      groupButtonText: `View all ${feed.departmentTitle} news`
    };

    return feed.carouselItems
      .filter(item => item.type !== 'viewall')
      .map(item => {
        // Handle member type items
        if (item.type === 'member') {
          return {
            id: item.id,
            name: item.title,
            type: 'member',
            memberType: item.memberType,
            displayType: 'carousel',
            ...groupInfo
          };
        }
        // Handle regular feed items
        return {
          id: item.id,
          name: item.title,
          displayType: 'carousel',
          apiParams: item.apiEndpoint,
          apiFunction: () => <div>Feed function not available</div>,
          feedBranch: item.branch,
          feedTopic: item.topic,
          buttonName: item.buttonName,
          generateFunction: null,
          useCommitteeAPI: item.useCommitteeAPI || false,  // ADDED
          ...groupInfo
        };
      });
  }
  
  return null;
};

export const validateMobileFeed = (mobileFeed) => {
  const requiredFields = ['id', 'name', 'displayType', 'apiParams', 'apiFunction', 'feedBranch', 'feedTopic', 'buttonName'];
  return requiredFields.every(field => mobileFeed.hasOwnProperty(field));
};

export const getGroupedCarouselFeeds = (mobileFeeds) => {
  return mobileFeeds
    .filter(feed => feed.displayType === 'carousel')
    .reduce((acc, feed) => {
      if (feed.group) {
        if (!acc[feed.group]) {
          acc[feed.group] = {
            displayName: feed.groupDisplayName,
            link: feed.groupLink,
            showGroupButton: feed.showGroupButton || false,
            groupButtonText: feed.groupButtonText || '',
            items: []
          };
        }
        acc[feed.group].items.push(feed);
      }
      return acc;
    }, {});
};

/**
 * Example usage function
 */
export const exampleUsage = (originalFeedsConfig) => {
  // Transform entire config
  const mobileFeedsConfig = transformFeedsToMobile(originalFeedsConfig);
  
  // Get feeds for specific section and country
  const legislativeFeeds = getMobileFeedsForSection(originalFeedsConfig, 'legislative', 'us');
  const executiveFeeds = getMobileFeedsForSection(originalFeedsConfig, 'executive', 'us');
  
  // Get Canada feeds
  const canadaExecutiveFeeds = getMobileFeedsForSection(null, 'executive', 'ca');
  
  // Get Mexico feeds
  const mexicoExecutiveFeeds = getMobileFeedsForSection(null, 'executive', 'mx');
  const mexicoLegislativeFeeds = getMobileFeedsForSection(null, 'legislative', 'mx');
  
  // Get only normal feeds for a section
  const normalFeeds = getMobileFeedsByType(originalFeedsConfig, 'legislative', 'normal', 'us');
  
  // Get grouped carousel feeds
  const groupedFeeds = getGroupedCarouselFeeds(executiveFeeds);
  
  return {
    mobileFeedsConfig,
    legislativeFeeds,
    executiveFeeds,
    canadaExecutiveFeeds,
    mexicoExecutiveFeeds,
    mexicoLegislativeFeeds,
    normalFeeds,
    groupedFeeds
  };
};